<template>
  <div class="labels-manager">
    <div v-if="loading" class="muted" style="margin-bottom: 12px">{{ __('Loading…') }}</div>
    <div v-else-if="labels.length === 0" class="muted" style="margin-bottom: 12px">
      {{ emptyMessage }}
    </div>
    <ul v-else class="labels-list">
      <li v-for="label in labels" :key="label.id" class="label-item">
        <div class="label-main">
          <div class="label-top-row">
            <span class="label-badge" :style="{ backgroundColor: label.color, color: '#fff' }">
              {{ label.title }}
            </span>
          </div>
          <div v-if="!createOnly" class="muted label-meta">
            {{
              n__('%d task', '%d tasks', label.tasksCount || 0).replace('%d', label.tasksCount || 0)
            }}
          </div>
        </div>
        <div v-if="!createOnly" class="label-actions">
          <button class="btn-sm" :disabled="disabled" @click="editLabel(label)">
            <span class="btn-text">{{ s__('ProjectSettings|Edit') }}</span>
            <span><EditIcon /></span>
          </button>
          <button class="btn-sm btn-danger" :disabled="disabled" @click="deleteLabel(label)">
            <span class="btn-text">{{ s__('ProjectSettings|Delete') }}</span>
            <span><TrashIcon /></span>
          </button>
        </div>
      </li>
    </ul>
    <button v-if="showCreateButton" :disabled="disabled" @click="openCreateForm">
      {{ addButtonText }}
    </button>

    <LabelModal
      v-model="showCreateModal"
      :title="addButtonText"
      :creating="submittingLabel"
      :submit-label="s__('ProjectSettings|Create')"
      :creating-label="s__('ProjectSettings|Creating...')"
      :initial-title="newLabel.title"
      :initial-color="newLabel.color"
      :error="labelError"
      @submit="createLabel"
      @close="cancelNewLabel"
    />

    <LabelModal
      v-model="showEditModal"
      :title="s__('ProjectSettings|Edit Label')"
      :creating="submittingLabel"
      :submit-label="s__('ProjectSettings|Save')"
      :creating-label="s__('ProjectSettings|Saving...')"
      :initial-title="editingLabel?.title || ''"
      :initial-color="editingLabel?.color || '#3B82F6'"
      :error="labelError"
      @submit="updateLabel"
      @close="cancelEditLabel"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, n__ } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import EditIcon from './icons/EditIcon.vue'
import TrashIcon from './icons/TrashIcon.vue'
import LabelModal from './LabelModal.vue'

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'user'].includes(value),
  },
  projectId: {
    type: String,
    required: false,
    default: null,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  addButtonText: {
    type: String,
    default: '',
  },
  createOnly: {
    type: Boolean,
    default: false,
    description: 'If true, hide edit/delete buttons and only show create form',
  },
  showCreateButton: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:expanded', 'refetch'])

// State
const labels = ref([])
const showCreateModal = ref(false)
const showEditModal = ref(false)
const newLabel = ref({ title: '', color: '#3B82F6' })
const editingLabel = ref(null)
const submittingLabel = ref(false)
const labelError = ref('')

// GraphQL queries based on scope
const PROJECT_LABELS_QUERY = gql`
  query ProjectLabels($id: ID!) {
    project(id: $id) {
      id
      labels {
        id
        title
        color
        tasksCount
      }
    }
  }
`

const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
      tasksCount
    }
  }
`

// GraphQL mutations based on scope
const CREATE_PROJECT_LABEL = gql`
  mutation CreateLabel($projectId: ID!, $title: String!, $color: String!) {
    createLabel(projectId: $projectId, title: $title, color: $color) {
      label {
        id
        title
        color
        tasksCount
      }
      errors
    }
  }
`

const UPDATE_PROJECT_LABEL = gql`
  mutation UpdateLabel($id: ID!, $title: String, $color: String) {
    updateLabel(id: $id, title: $title, color: $color) {
      label {
        id
        title
        color
        tasksCount
      }
      errors
    }
  }
`

const DELETE_PROJECT_LABEL = gql`
  mutation DeleteLabel($id: ID!) {
    deleteLabel(id: $id) {
      label {
        id
      }
      errors
    }
  }
`

const CREATE_PERSONAL_LABEL = gql`
  mutation CreatePersonalLabel($title: String!, $color: String!) {
    createPersonalLabel(title: $title, color: $color) {
      personalLabel {
        id
        title
        color
        tasksCount
      }
      errors
    }
  }
`

const UPDATE_PERSONAL_LABEL = gql`
  mutation UpdatePersonalLabel($id: ID!, $title: String, $color: String) {
    updatePersonalLabel(id: $id, title: $title, color: $color) {
      personalLabel {
        id
        title
        color
        tasksCount
      }
      errors
    }
  }
`

const DELETE_PERSONAL_LABEL = gql`
  mutation DeletePersonalLabel($id: ID!) {
    deletePersonalLabel(id: $id) {
      personalLabel {
        id
      }
      errors
    }
  }
`

// Setup queries and mutations based on scope
const query = computed(() => {
  return props.scope === 'project' ? PROJECT_LABELS_QUERY : PERSONAL_LABELS_QUERY
})

const createMutation = computed(() => {
  return props.scope === 'project' ? CREATE_PROJECT_LABEL : CREATE_PERSONAL_LABEL
})

const updateMutation = computed(() => {
  return props.scope === 'project' ? UPDATE_PROJECT_LABEL : UPDATE_PERSONAL_LABEL
})

const deleteMutation = computed(() => {
  return props.scope === 'project' ? DELETE_PROJECT_LABEL : DELETE_PERSONAL_LABEL
})

// Lazy query setup
const {
  load: loadLabels,
  result: labelsResult,
  loading: labelsLoading,
  refetch: refetchLabels,
} = useLazyQuery(
  query,
  () => {
    if (props.scope === 'project') {
      return { id: props.projectId }
    }
    return {}
  },
  () => ({
    enabled: props.expanded && (props.scope === 'user' || !!props.projectId),
  })
)

// Mutations
const { mutate: createLabelMutation } = useMutation(createMutation, {
  errorPolicy: 'all', // Ensure we get partial data and errors even if GraphQL errors occur
})
const { mutate: updateLabelMutation } = useMutation(updateMutation)
const { mutate: deleteLabelMutation } = useMutation(deleteMutation)

// Computed
const loading = computed(() => labelsLoading.value)

// Watch for expanded prop changes to trigger loading
watch(
  () => props.expanded,
  expanded => {
    if (expanded && !labelsResult.value && !labelsLoading.value) {
      if (props.scope === 'project' && props.projectId) {
        loadLabels()
      } else if (props.scope === 'user') {
        loadLabels()
      }
    }
  },
  { immediate: true }
)

// Watch for query results
watch(
  labelsResult,
  data => {
    if (props.scope === 'project' && data?.project?.labels) {
      labels.value = data.project.labels || []
    } else if (props.scope === 'user' && data?.personalLabels) {
      labels.value = data.personalLabels || []
    }
  },
  { immediate: true }
)

// Actions
function editLabel(label) {
  editingLabel.value = { id: label.id, title: label.title, color: label.color }
  labelError.value = ''
  showEditModal.value = true
}

function cancelEditLabel() {
  showEditModal.value = false
  editingLabel.value = null
  labelError.value = ''
}

function cancelNewLabel() {
  showCreateModal.value = false
  newLabel.value = { title: '', color: '#3B82F6' }
  labelError.value = ''
}

async function createLabel(payload) {
  const title = payload?.title?.trim() || ''
  const color = payload?.color || '#3B82F6'
  if (!title || !/^#[0-9A-F]{6}$/i.test(color)) return

  labelError.value = ''
  submittingLabel.value = true
  try {
    const mutationVars =
      props.scope === 'project'
        ? {
            projectId: props.projectId,
            title,
            color: color.toUpperCase(),
          }
        : {
            title,
            color: color.toUpperCase(),
          }

    const { data, errors: graphqlErrors } = await createLabelMutation(mutationVars)

    const resultField = props.scope === 'project' ? 'createLabel' : 'createPersonalLabel'
    const labelField = props.scope === 'project' ? 'label' : 'personalLabel'

    // Prioritize errors returned by the mutation itself (e.g., validation errors from service)
    if (data?.[resultField]?.errors?.length) {
      labelError.value = data[resultField].errors.join(', ')
      return
    }

    // If there are GraphQL errors (e.g., null for non-nullable field due to validation failure)
    if (graphqlErrors && graphqlErrors.length > 0) {
      // Attempt to find a more user-friendly message
      const errorMessage = graphqlErrors[0]?.message || 'Failed to create label'
      if (errorMessage.includes('null for non-nullable')) {
        // This often indicates a validation failure where the object wasn't created
        // Use the same approach as useCreateLabel.js - provide user-friendly message
        labelError.value =
          props.scope === 'project'
            ? 'A label with this title already exists'
            : 'A personal label with this title already exists'
      } else {
        labelError.value = errorMessage
      }
      return
    }

    const label = data?.[resultField]?.[labelField]
    if (!label) {
      // Fallback if no label is returned and no explicit errors were caught
      labelError.value =
        props.scope === 'project'
          ? s__('Label|Failed to create label')
          : s__('PersonalLabel|Failed to create personal label')
      return
    }

    cancelNewLabel()

    // Refetch to get the updated list from server
    if (props.expanded) {
      // Use refetch if query has been executed, otherwise load
      if (labelsResult.value && refetchLabels) {
        await refetchLabels()
      } else {
        await loadLabels()
      }
    }

    // Emit refetch event for parent
    emit('refetch')
  } catch (e) {
    // Catch any other unexpected errors (e.g., network issues)
    labelError.value = e.message || 'Failed to create label'
  } finally {
    submittingLabel.value = false
  }
}

function openCreateForm() {
  if (props.disabled) return
  editingLabel.value = null
  labelError.value = ''
  newLabel.value = { title: '', color: '#3B82F6' }
  showCreateModal.value = true
}

async function updateLabel(payload) {
  if (!editingLabel.value) return
  const title = payload?.title?.trim() || ''
  const color = payload?.color || '#3B82F6'
  if (!title || !/^#[0-9A-F]{6}$/i.test(color)) return

  labelError.value = ''
  submittingLabel.value = true
  try {
    const { data } = await updateLabelMutation({
      id: editingLabel.value.id,
      title,
      color: color.toUpperCase(),
    })

    const resultField = props.scope === 'project' ? 'updateLabel' : 'updatePersonalLabel'
    const labelField = props.scope === 'project' ? 'label' : 'personalLabel'

    if (data?.[resultField]?.errors?.length) {
      throw new Error(data[resultField].errors.join(', '))
    }

    cancelEditLabel()

    // Refetch to ensure we have the latest data with correct tasksCount
    if (props.expanded) {
      if (refetchLabels && typeof refetchLabels === 'function') {
        await refetchLabels()
      } else if (labelsResult.value) {
        // If query has been executed, try to refetch
        await loadLabels()
      } else {
        await loadLabels()
      }
    }

    // Emit refetch event for parent
    emit('refetch')
  } catch (e) {
    labelError.value = e.message || 'Failed to update label'
  } finally {
    submittingLabel.value = false
  }
}

async function deleteLabel(label) {
  const confirmed = await showConfirm(`Delete label "${label.title}"?`)
  if (!confirmed) return
  try {
    const { data } = await deleteLabelMutation({ id: label.id })

    const resultField = props.scope === 'project' ? 'deleteLabel' : 'deletePersonalLabel'

    if (data?.[resultField]?.errors?.length) {
      throw new Error(data[resultField].errors.join(', '))
    }

    // Refetch to get the updated list from server (label will be removed)
    if (props.expanded) {
      // Use refetch if query has been executed, otherwise load
      if (labelsResult.value && refetchLabels) {
        await refetchLabels()
      } else {
        await loadLabels()
      }
    }

    // Emit refetch event for parent
    emit('refetch')
  } catch (e) {
    alert('Failed to delete label: ' + (e.message || 'Unknown error'))
  }
}

// Expose methods for parent components
defineExpose({
  loadLabels,
  refetch: refetchLabels || loadLabels,
  openCreateForm,
})
</script>

<style scoped>
.labels-manager {
  width: 100%;
}

.labels-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
}

.label-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.label-main {
  flex: 1;
  min-width: 0;
}

.label-top-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-meta {
  font-size: 13px;
  margin-top: 4px;
}

.label-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
  align-items: center;
}

/* Button styles inherit from forms.css */

.muted {
  color: #999;
  font-size: 14px;
}

.error {
  color: #d32f2f;
  font-size: 14px;
}
</style>
